import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import { usePeriod } from '@/lib/period';
import { PeriodControl } from '@/features/shared/PeriodControl';
import { BackfillButton } from '@/features/shared/BackfillButton';
import { OpenInPostHogButton } from '@/components/OpenInPostHogButton';
import { posthogTrendUrl } from '@/lib/posthogLinks';
import { useOperation, type OperationSummary } from './useOperation';

/** `{a: 3, b: 1}` → `[['a',3],['b',1]]` ordenado por valor desc. */
function sortedRows(rec: Record<string, number>): { key: string; count: number }[] {
  return Object.entries(rec)
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count);
}

type ProtocolRow = {
  protocol: string;
  prints: number;
  failures: number;
  successPct: number | null;
};

function protocolSuccessRows(summary: OperationSummary): ProtocolRow[] {
  const protocols = new Set([
    ...Object.keys(summary.printsByProtocol),
    ...Object.keys(summary.failuresByProtocol),
  ]);
  return [...protocols]
    .map((protocol) => {
      const prints = summary.printsByProtocol[protocol] ?? 0;
      const failures = summary.failuresByProtocol[protocol] ?? 0;
      const total = prints + failures;
      return {
        protocol,
        prints,
        failures,
        successPct: total > 0 ? prints / total : null,
      };
    })
    .sort((a, b) => b.prints + b.failures - (a.prints + a.failures));
}

function CountTable({
  rec,
  keyHeader,
  valueHeader,
  empty,
}: {
  rec: Record<string, number>;
  keyHeader: string;
  valueHeader: string;
  empty: string;
}): React.JSX.Element {
  return (
    <Table
      variant="embedded"
      items={sortedRows(rec)}
      empty={<Box color="text-status-inactive">{empty}</Box>}
      columnDefinitions={[
        { id: 'key', header: keyHeader, cell: (r): string => r.key },
        { id: 'count', header: valueHeader, cell: (r): string => String(r.count) },
      ]}
    />
  );
}

/**
 * Página /dashboard/operation. Consome `/aggregations/operation/days/`
 * (aggregator `aggregateOperation`, dos eventos PostHog do Android e do iOS). 6 cards
 * com dados reais — distribuição por protocolo, por tamanho, falhas por
 * motivo, taxa de sucesso por protocolo, conexões de impressora (total do
 * período) e modelos (de `printer_connected`).
 *
 * Origem: sem issue — commit f8a89a64d, 2026-05-04 (rota com placeholders);
 * dados reais desde 9ddffdfe8 (#68, 2026-06-20).
 *
 * Contrato: sem props; período pelo `usePeriod`. Estados LOADING/ERROR/READY
 * vêm de `useOperation`. Ações no cabeçalho: abrir no PostHog e recalcular o
 * período (`BackfillButton type="operation"`, a única escrita).
 *
 * Usado por: `routes/router.tsx` (rota `dashboard/operation`, só super-admin).
 *
 * Arquitetura: só apresentação; leitura no `useOperation`, reprocessamento
 * pela callable `backfillAggregations`. A origem dos números é o PostHog do
 * Android e do iOS — telemetria, não o Firestore de impressões (`printLogs`).
 *
 * Dados: o iOS aparece como a linha `BLE_GATT` nos cards de protocolo e com
 * o `errorCode` dele nos motivos de falha (#939). A taxa de sucesso do
 * `BLE_GATT` é aproximada: a impressão conta etiquetas, mas o `print_failed`
 * do iOS sai um por lote. Tamanho de etiqueta é só Android.
 *
 * Issues: #68 (dashboard de Operação, cards de conexão, hardening), #939
 * (iOS no agregado).
 */
export function OperationDashboardPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { period, setPeriod } = usePeriod();
  const state = useOperation(period);

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t('operation.subtitle')}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <OpenInPostHogButton
                href={posthogTrendUrl([{ id: 'print_executed' }])}
                surface="operation"
              />
              <BackfillButton type="operation" />
            </SpaceBetween>
          }
        >
          {t('operation.title')}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <PeriodControl value={period} onChange={setPeriod} />

        {state.kind === 'LOADING' && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
          </Box>
        )}
        {state.kind === 'ERROR' && <Alert type="error">{state.error}</Alert>}
        {state.kind === 'READY' && (
          <SpaceBetween size="l">
            <KeyValuePairs
              columns={2}
              items={[
                {
                  label: t('operation.totalPrints'),
                  value: String(state.summary.totalPrints),
                },
                {
                  label: t('operation.totalFailures'),
                  value: String(state.summary.totalFailures),
                },
              ]}
            />
            <ColumnLayout columns={2}>
              <Container
                header={
                  <Header variant="h3" description={t('operation.hint.protocolPie')}>
                    {t('operation.section.protocolPie')}
                  </Header>
                }
              >
                <CountTable
                  rec={state.summary.printsByProtocol}
                  keyHeader={t('operation.col.protocol')}
                  valueHeader={t('operation.col.prints')}
                  empty={t('operation.placeholder')}
                />
              </Container>

              <Container
                header={
                  <Header variant="h3" description={t('operation.hint.labelSize')}>
                    {t('operation.section.labelSize')}
                  </Header>
                }
              >
                <CountTable
                  rec={state.summary.printsBySize}
                  keyHeader={t('operation.col.size')}
                  valueHeader={t('operation.col.prints')}
                  empty={t('operation.placeholder')}
                />
              </Container>

              <Container
                header={
                  <Header variant="h3" description={t('operation.hint.protocolSuccess')}>
                    {t('operation.section.protocolSuccess')}
                  </Header>
                }
              >
                <Table
                  variant="embedded"
                  items={protocolSuccessRows(state.summary)}
                  empty={<Box color="text-status-inactive">{t('operation.placeholder')}</Box>}
                  columnDefinitions={[
                    { id: 'protocol', header: t('operation.col.protocol'), cell: (r): string => r.protocol },
                    { id: 'prints', header: t('operation.col.prints'), cell: (r): string => String(r.prints) },
                    { id: 'failures', header: t('operation.col.failures'), cell: (r): string => String(r.failures) },
                    {
                      id: 'success',
                      header: t('operation.col.successRate'),
                      cell: (r): string =>
                        r.successPct === null ? '—' : `${(r.successPct * 100).toFixed(1)}%`,
                    },
                  ]}
                />
              </Container>

              <Container
                header={
                  <Header variant="h3" description={t('operation.hint.printFailures')}>
                    {t('operation.section.printFailures')}
                  </Header>
                }
              >
                <CountTable
                  rec={state.summary.failuresByReason}
                  keyHeader={t('operation.col.reason')}
                  valueHeader={t('operation.col.failures')}
                  empty={t('operation.placeholder')}
                />
              </Container>

              <Container
                header={
                  <Header variant="h3" description={t('operation.hint.printersConnected')}>
                    {t('operation.section.printersConnected')}
                  </Header>
                }
              >
                <Box variant="awsui-value-large" textAlign="center" padding="l">
                  {String(state.summary.totalConnections)}
                </Box>
              </Container>

              <Container
                header={
                  <Header variant="h3" description={t('operation.hint.printerModels')}>
                    {t('operation.section.printerModels')}
                  </Header>
                }
              >
                <CountTable
                  rec={state.summary.connectionsByModel}
                  keyHeader={t('operation.col.model')}
                  valueHeader={t('operation.col.connections')}
                  empty={t('operation.placeholder')}
                />
              </Container>
            </ColumnLayout>
          </SpaceBetween>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
}
